import {
  Button,
  CollapseChevronContainer,
  CollapseChevronIcon,
  CollapseContainer,
  CollapseDropdownContainer,
  CollapseTitleContainer,
  CollapseTitleRow,
  ControlledCollapse,
  Label,
  ThemeProp,
} from '@trarc/pc-shared';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { DocumentIcon, HazardIcon, HelpIcon, HouseIcon } from '../../../assets';

export interface PageGroupProps {
  isExpanded: boolean;
}

interface StyledSideBarProps {
  $isExpanded?: boolean;
}

interface StyledCollapseProps {
  open?: boolean;
  $isActive?: boolean;
}

const iconSize = `1.4rem`;

const iconStyles = css`
  stroke: ${({ $isExpanded, theme }: StyledSideBarProps & ThemeProp) =>
    $isExpanded ? theme.colors.primary : theme.colors.c400};
  height: ${iconSize};
  width: ${iconSize};
`;

const fillStyles = css`
  fill: ${({ $isExpanded, theme }: StyledSideBarProps & ThemeProp) =>
    $isExpanded ? theme.colors.primary : theme.colors.c400};
`;

export const StyledHouseIcon = styled(HouseIcon)<StyledSideBarProps>`
  ${iconStyles}
`;

export const StyledDocumentIcon = styled(DocumentIcon)<StyledSideBarProps>`
  ${iconStyles}
`;

export const StyledHazardIcon = styled(HazardIcon)<StyledSideBarProps>`
  ${iconStyles}
  ${fillStyles}
`;

export const StyledHelpIcon = styled(HelpIcon)<StyledSideBarProps>`
  ${iconStyles}
  ${fillStyles}
`;

export const PageContainerRow = styled.div<StyledCollapseProps>`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const PageGroupIconContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const PageGroupTitleContainer = styled.div<{ open: boolean; $isActive: boolean; $isExpanded: boolean }>`
  display: flex;
  opacity: ${({ $isExpanded }: StyledSideBarProps) => ($isExpanded ? '1' : '0')};
  color: ${({ open, $isActive, theme }: StyledCollapseProps & ThemeProp) => {
    if ($isActive) {
      return theme.colors.primary;
    } else if (open) {
      return theme.colors.c500;
    } else {
      return undefined;
    }
  }};

  transition: opacity ease-out 0.5s;
`;

const collapseStyles = css`
  ${CollapseContainer} {
    width: 17rem;
  }

  ${CollapseTitleContainer} {
    flex-direction: row-reverse;
    justify-content: space-between;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
    font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
    color: ${(props: ThemeProp) => props.theme.colors.c400};
    padding-left: 0.9rem;
    height: 1rem;

    ${CollapseChevronIcon} {
      stroke: ${({ open, $isActive, theme }: StyledCollapseProps & ThemeProp) => {
        if ($isActive) {
          return theme.colors.primary;
        } else if (open) {
          return theme.colors.c500;
        } else {
          return theme.colors.c400;
        }
      }};
      transform: ${({ open }) => (open ? 'rotate(90deg)' : 'rotate(0deg)')};

      transition: all ease-out 0.15s;
    }
  }

  ${CollapseTitleRow} {
    width: 16.4rem;
    height: 2.5rem;
    border: none;
    border-radius: 0.5rem;
    background-color: ${({ open, $isActive, theme }: StyledCollapseProps & ThemeProp) => {
      if ($isActive) {
        return `${theme.colors.primary}15`;
      } else if (open) {
        return theme.colors.blackLowOpacity;
      } else {
        return 'transparent';
      }
    }};

    &:hover {
      background-color: ${({ theme }: ThemeProp) => theme.colors.blackLowOpacity};
      ${PageGroupTitleContainer} {
        color: ${({ theme }: ThemeProp) => theme.colors.c500};
      }
      ${CollapseChevronIcon}, ${StyledHouseIcon}, ${StyledDocumentIcon} {
        stroke: ${({ theme }: ThemeProp) => theme.colors.c500};
      }
      ${StyledHelpIcon}, ${StyledHazardIcon} {
        stroke: ${({ theme }: ThemeProp) => theme.colors.c500};
        fill: ${({ theme }: ThemeProp) => theme.colors.c500};
      }
    }
  }

  ${CollapseDropdownContainer} {
    background-color: ${(props: ThemeProp) => props.theme.colors.white};
    padding-left: 3.25rem;
    padding-bottom: 0;
    width: 16rem;
    border: none;
    gap: 0.5rem;

    transition: all ease-out 0.3s allow-discrete;

    @starting-style {
      opacity: 0;
    }
  }

  ${CollapseChevronContainer} {
    transform: rotate(-0.25turn);
    padding: 1rem 1rem 0.5rem 1rem;
  }
`;

export const StyledControlledCollapse = styled(ControlledCollapse)<{
  open: boolean;
  $isExpanded: boolean;
  $isActive?: boolean;
}>`
  ${collapseStyles}
`;

export const StyledNavLink = styled(NavLink)`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  color: ${(props: ThemeProp) => props.theme.colors.c400};
  height: 2rem;
  border-radius: 0.5rem;
  padding: 0.5rem;

  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.blackLowOpacity};
  }

  &.active {
    background: ${(props: ThemeProp) => props.theme.colors.secondary}15;
    color: ${(props: ThemeProp) => props.theme.colors.primary};
  }
`;

export const StyledButton = styled(Button)`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  background-color: ${(props: ThemeProp) => props.theme.colors.white};
  justify-content: left;
  height: 2rem;
  border-radius: 0.5rem;
  padding: 0.5rem;

  ${Label} {
    color: ${(props: ThemeProp) => props.theme.colors.c400};
  }

  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.blackLowOpacity};
  }

  &.active {
    background: ${(props: ThemeProp) => props.theme.colors.secondary}15;
    color: ${(props: ThemeProp) => props.theme.colors.primary};
  }
`;
